// Deskte AI — the agency's conversational assistant. Self-contained chat that
// talks to /ai/agent (api.aiAgent). Reused in two places:
//   • DeskteAiWidget — a floating panel toggled from a top-bar icon.
//   • The "Deskte AI" sidebar page (view==="deskte_ai").
//
// The assistant's display name is configurable per agency
// (AgencySetting.aiAssistantName); callers pass it in as `aiName`.
//
// ACTIONS (behind the AI_ACTIONS_ENABLED backend flag): /ai/agent may return
// `proposals` to create/update a client. The AI never writes — we render a
// confirmation card and only on the agent's "Confirmar" do we apply it through
// the normal client APIs (createClient/updateClient), same validation and
// permissions as the forms. `openClient` scopes "update" to the open ficha.

import { useState, useRef, useEffect } from 'react';
import { Send, Check, X, History, MessageSquare, RotateCcw } from 'lucide-react';
import { aiAgent as callAiAgent, createClient, updateClient, createPolicy, updatePolicy, getClient, getPolicy, deleteClient, deletePolicy, aiBulkPolicyUpdate, aiUndoBulkPolicy, aiBulkClientUpdate, aiUndoBulkClient } from '../api.js';
import { recordAiAction, updateAiAction, getAiActions, getUndoPayload } from '../aiActionHistory.js';
import { getAiOpenContext } from '../aiContext.js';

export default function DeskteAiChat({ aiName = 'Deskte AI', lang = 'en', embedded = false, openClient = null, openPolicy = null, onApplied = null }) {
  const es = lang === 'es';
  const greeting = es
    ? `👋 ¡Hola! Soy ${aiName}, tu asistente inteligente. Puedo ayudarte a:\n\n• Calificar leads\n• Redactar follow-ups\n• Responder dudas de clientes\n• Analizar tu pipeline\n\n¿En qué te ayudo?`
    : `👋 Hi! I'm ${aiName}, your intelligent assistant. I can help you:\n\n• Qualify leads\n• Draft follow-ups\n• Answer client questions\n• Analyze your pipeline\n\nHow can I help?`;

  const [chat, setChat] = useState([{ role: 'ai', text: greeting }]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyTick, setHistoryTick] = useState(0);
  const scrollRef = useRef(null);

  // Human title for a proposal or a history entry (both carry action + data).
  const titleFor = (action, d = {}) => {
    const t = {
      create_client: es ? '🆕 Cliente creado' : '🆕 Client created',
      update_client: es ? `✏️ Cliente modificado: ${d.clientName || ''}` : `✏️ Client updated: ${d.clientName || ''}`,
      create_policy: es ? `🆕 Póliza creada para ${d.clientName || ''}` : `🆕 Policy created for ${d.clientName || ''}`,
      update_policy: es ? `✏️ Póliza modificada: ${d.policyLabel || ''}` : `✏️ Policy updated: ${d.policyLabel || ''}`,
      bulk_update_policies: es ? `🔁 Cambio masivo — ${d.count || 0} pólizas` : `🔁 Bulk update — ${d.count || 0} policies`,
      bulk_update_clients: es ? `🔁 Cambio masivo — ${d.count || 0} clientes` : `🔁 Bulk update — ${d.count || 0} clients`,
    };
    return t[action] || (es ? 'Acción' : 'Action');
  };

  // Keep the greeting in sync if the name/language changes while mounted, but
  // only when the conversation hasn't started yet (don't clobber history).
  useEffect(() => {
    setChat((prev) => (prev.length === 1 && prev[0].role === 'ai' ? [{ role: 'ai', text: greeting }] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiName, lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, thinking]);

  const send = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = input.trim();
    const prior = chat;
    setChat((p) => [...p, { role: 'user', text: userMsg }]);
    setInput('');
    setThinking(true);
    try {
      const history = prior.filter((m) => m.role === 'user' || m.role === 'ai').slice(-6);
      // Prefer explicit props; otherwise fall back to whatever ficha is open on
      // screen (set by the detail views) so "modify THIS one" works from the widget.
      const shared = getAiOpenContext();
      const oc = (openClient && openClient.id) ? openClient : shared.openClient;
      const op = (openPolicy && openPolicy.id) ? openPolicy : shared.openPolicy;
      const context = {};
      if (oc && oc.id) context.openClient = { id: oc.id, name: oc.name || '' };
      if (op && op.id) context.openPolicy = { id: op.id };
      const { reply, proposals } = await callAiAgent({ message: userMsg, history, context: Object.keys(context).length ? context : undefined });
      setChat((p) => [...p, {
        role: 'ai',
        text: reply || (es ? '(sin respuesta)' : '(no reply)'),
        proposals: Array.isArray(proposals) ? proposals : [],
      }]);
    } catch (e) {
      const msg = String((e && e.message) || e);
      setChat((p) => [...p, { role: 'ai', text: (es ? '⚠️ No pude responder: ' : "⚠️ Couldn't respond: ") + msg }]);
    } finally {
      setThinking(false);
    }
  };

  // Apply a confirmed proposal. Updates send the FULL merged record (fetch
  // current → spread the changes on top) so a partial patch can never wipe the
  // other fields via mapClientToApi/mapPolicyToApi defaults. Each apply also
  // builds a generic `undo` descriptor so ANY action can be reverted.
  const applyProposal = async (msgIdx, proposal) => {
    setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'applying' } : m)));
    let undo = null;
    try {
      if (proposal.action === 'create_client') {
        const created = await createClient(proposal.fields || {});
        if (created && created.id) undo = { kind: 'delete_client', id: created.id };
      } else if (proposal.action === 'update_client') {
        if (!proposal.clientId) throw new Error(es ? 'No hay cliente' : 'No client');
        const cur = await getClient(proposal.clientId);
        const prev = {};
        for (const k of Object.keys(proposal.fields || {})) prev[k] = cur ? cur[k] : undefined;
        await updateClient(proposal.clientId, { ...(cur || {}), ...(proposal.fields || {}) });
        undo = { kind: 'restore_client', clientId: proposal.clientId, prev };
      } else if (proposal.action === 'create_policy') {
        if (!proposal.clientId) throw new Error(es ? 'No hay cliente' : 'No client');
        const created = await createPolicy({ clientId: proposal.clientId, ...(proposal.fields || {}) });
        if (created && created.id) undo = { kind: 'delete_policy', id: created.id };
      } else if (proposal.action === 'update_policy') {
        if (!proposal.policyId) throw new Error(es ? 'No hay póliza' : 'No policy');
        const cur = await getPolicy(proposal.policyId);
        const prev = {};
        for (const k of Object.keys(proposal.fields || {})) prev[k] = cur ? cur[k] : undefined;
        await updatePolicy(proposal.policyId, { ...(cur || {}), ...(proposal.fields || {}) });
        undo = { kind: 'restore_policy', policyId: proposal.policyId, prev };
      } else if (proposal.action === 'bulk_update_policies') {
        const res = await aiBulkPolicyUpdate({ filter: proposal.filter || {}, fields: proposal.fields || {} });
        const snapshot = res && Array.isArray(res.snapshot) ? res.snapshot : null;
        if (snapshot && snapshot.length) undo = { kind: 'bulk', snapshot };
      } else if (proposal.action === 'bulk_update_clients') {
        const res = await aiBulkClientUpdate({ filter: proposal.filter || {}, fields: proposal.fields || {} });
        const snapshot = res && Array.isArray(res.snapshot) ? res.snapshot : null;
        if (snapshot && snapshot.length) undo = { kind: 'bulk_clients', snapshot };
      } else {
        throw new Error(es ? 'Acción no soportada' : 'Unsupported action');
      }
      const histId = 'h_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      recordAiAction({
        id: histId,
        at: Date.now(),
        action: proposal.action,
        clientName: proposal.clientName || '',
        policyLabel: proposal.policyLabel || '',
        count: proposal.count || 0,
        fields: proposal.fields || {},
        filter: proposal.filter || null,
        summary: proposal.summary || '',
        status: 'applied',
        undoable: !!undo,
      }, undo); // undo payload stored in its own localStorage key
      setHistoryTick((t) => t + 1);
      setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'done', undo, histId } : m)));
      if (typeof onApplied === 'function') onApplied(proposal);
    } catch (e) {
      const msg = String((e && e.message) || e);
      setChat((p) => p.map((m, i) => (i === msgIdx
        ? { ...m, proposalState: 'error', proposalError: msg }
        : m)));
    }
  };

  // Revert an applied action from its undo descriptor. Updates restore the old
  // values on top of the CURRENT record (so other edits since aren't lost);
  // creates are deleted; bulk restores its per-policy snapshot.
  const performUndo = async (undo) => {
    if (!undo || !undo.kind) throw new Error(es ? 'Nada que deshacer' : 'Nothing to undo');
    if (undo.kind === 'bulk') {
      await aiUndoBulkPolicy({ snapshot: undo.snapshot || [] });
    } else if (undo.kind === 'bulk_clients') {
      await aiUndoBulkClient({ snapshot: undo.snapshot || [] });
    } else if (undo.kind === 'restore_client') {
      const cur = await getClient(undo.clientId);
      await updateClient(undo.clientId, { ...(cur || {}), ...(undo.prev || {}) });
    } else if (undo.kind === 'restore_policy') {
      const cur = await getPolicy(undo.policyId);
      await updatePolicy(undo.policyId, { ...(cur || {}), ...(undo.prev || {}) });
    } else if (undo.kind === 'delete_client') {
      await deleteClient(undo.id);
    } else if (undo.kind === 'delete_policy') {
      await deletePolicy(undo.id);
    } else {
      throw new Error(es ? 'No se puede deshacer' : 'Cannot undo');
    }
  };

  // Undo from the chat card (in-session).
  const undoProposal = async (msgIdx, undo, histId) => {
    if (!undo) return;
    setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'undoing' } : m)));
    try {
      await performUndo(undo);
      setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'undone' } : m)));
      if (histId) { updateAiAction(histId, { status: 'undone', undoable: false }); setHistoryTick((t) => t + 1); }
      if (typeof onApplied === 'function') onApplied({ action: 'undo' });
    } catch (e) {
      const emsg = String((e && e.message) || e);
      setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'error', proposalError: emsg } : m)));
    }
  };

  // Undo from the persistent Historial tab (survives refresh). The undo payload
  // lives in its own localStorage key; read it and dispatch generically.
  const undoFromHistory = async (entry) => {
    if (!entry || !entry.undoable) return;
    const undo = getUndoPayload(entry.id);
    if (!undo) {
      updateAiAction(entry.id, { undoable: false, undoError: es ? 'No hay datos para deshacer' : 'No undo data' });
      setHistoryTick((t) => t + 1);
      return;
    }
    updateAiAction(entry.id, { status: 'undoing' });
    setHistoryTick((t) => t + 1);
    try {
      await performUndo(undo);
      updateAiAction(entry.id, { status: 'undone', undoable: false });
      if (typeof onApplied === 'function') onApplied({ action: 'undo' });
    } catch (e) {
      updateAiAction(entry.id, { status: 'applied', undoError: String((e && e.message) || e) });
    } finally {
      setHistoryTick((t) => t + 1);
    }
  };

  const dismissProposal = (msgIdx) => {
    setChat((p) => p.map((m, i) => (i === msgIdx ? { ...m, proposalState: 'cancelled' } : m)));
  };

  const fieldLabel = (k) => {
    const map = es
      ? { firstName: 'Nombre', middleName: 'Segundo nombre', lastName: 'Apellido', email: 'Correo', phone: 'Teléfono', phoneSecondary: 'Teléfono 2', addressStreet: 'Dirección', addressCity: 'Ciudad', addressState: 'Estado', addressZip: 'Código postal', county: 'Condado', preferredLang: 'Idioma', status: 'Estatus', maritalStatus: 'Estado civil', employer: 'Empleador', employerPhone: 'Tel. empleador', householdSize: 'Personas en el hogar', notes: 'Notas', source: 'Fuente', type: 'Tipo', carrier: 'Compañía', plan: 'Plan', policyNumber: 'N.º de póliza', effectiveDate: 'Fecha efectiva', renewalDate: 'Fecha de renovación', monthlyPremium: 'Prima mensual', coverageAmount: 'Cobertura', deductible: 'Deducible', beneficiary: 'Beneficiario' }
      : { firstName: 'First name', middleName: 'Middle name', lastName: 'Last name', email: 'Email', phone: 'Phone', phoneSecondary: 'Phone 2', addressStreet: 'Address', addressCity: 'City', addressState: 'State', addressZip: 'ZIP', county: 'County', preferredLang: 'Language', status: 'Status', maritalStatus: 'Marital status', employer: 'Employer', employerPhone: 'Employer phone', householdSize: 'Household size', notes: 'Notes', source: 'Source', type: 'Type', carrier: 'Carrier', plan: 'Plan', policyNumber: 'Policy #', effectiveDate: 'Effective date', renewalDate: 'Renewal date', monthlyPremium: 'Monthly premium', coverageAmount: 'Coverage', deductible: 'Deductible', beneficiary: 'Beneficiary' };
    return map[k] || k;
  };

  const renderProposal = (msg, msgIdx) => {
    if (!Array.isArray(msg.proposals) || !msg.proposals.length) return null;
    const state = msg.proposalState || 'pending';
    return msg.proposals.map((pr, pi) => {
      const entries = Object.entries(pr.fields || {});
      const isBulk = pr.action === 'bulk_update_policies' || pr.action === 'bulk_update_clients';
      const bulkNoun = pr.action === 'bulk_update_clients' ? (es ? 'clientes' : 'clients') : (es ? 'pólizas' : 'policies');
      const titles = {
        create_client: es ? '🆕 Crear cliente nuevo' : '🆕 Create new client',
        update_client: es ? `✏️ Modificar cliente: ${pr.clientName || ''}` : `✏️ Update client: ${pr.clientName || ''}`,
        create_policy: es ? `🆕 Crear póliza para ${pr.clientName || ''}` : `🆕 Create policy for ${pr.clientName || ''}`,
        update_policy: es ? `✏️ Modificar póliza: ${pr.policyLabel || ''}` : `✏️ Update policy: ${pr.policyLabel || ''}`,
        bulk_update_policies: es ? `🔁 Cambio masivo — ${pr.count || 0} pólizas` : `🔁 Bulk update — ${pr.count || 0} policies`,
        bulk_update_clients: es ? `🔁 Cambio masivo — ${pr.count || 0} clientes` : `🔁 Bulk update — ${pr.count || 0} clients`,
      };
      const title = titles[pr.action] || (es ? 'Acción' : 'Action');
      const filterLabel = (k) => (es
        ? { carrier: 'Compañía', status: 'Estatus', type: 'Tipo', all: 'Todos', language: 'Idioma actual', state: 'Estado (US)', source: 'Fuente' }
        : { carrier: 'Carrier', status: 'Status', type: 'Type', all: 'All', language: 'Current language', state: 'US state', source: 'Source' })[k] || k;
      const filterEntries = isBulk
        ? Object.entries(pr.filter || {}).map(([k, v]) => [k, k === 'all' ? (es ? 'todo el libro' : 'whole book') : v])
        : [];
      return (
        <div key={pi} style={{ marginTop: '.5rem', marginLeft: 0, maxWidth: '95%', border: `1px solid ${isBulk ? '#e0a83a' : 'var(--c-purple,#a78bfa)'}`, background: isBulk ? 'rgba(224,168,58,.08)' : 'rgba(167,139,250,.08)', borderRadius: 10, padding: '.6rem .7rem' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', marginBottom: '.35rem' }}>{title}</div>
          {pr.summary ? <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#9fc1dd)', marginBottom: '.4rem', lineHeight: 1.4 }}>{pr.summary}</div> : null}
          {isBulk ? (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#9fc1dd)', marginBottom: '.4rem' }}>
              <strong>{es ? 'Afecta:' : 'Affects:'} {pr.count || 0} {bulkNoun}</strong>
              {filterEntries.length ? ' — ' + filterEntries.map(([k, v]) => `${filterLabel(k)}: ${v}`).join(', ') : ''}
            </div>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.15rem', marginBottom: '.5rem' }}>
            {entries.map(([k, v]) => (
              <div key={k} style={{ fontSize: '.72rem', color: 'var(--c-text,#c8dff0)' }}>
                <span style={{ color: 'var(--c-text3,#45688a)' }}>{fieldLabel(k)}:</span> <strong>{String(v)}</strong>
              </div>
            ))}
          </div>
          {state === 'done' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.74rem', color: '#00c37a', fontWeight: 700 }}>✅ {es ? 'Aplicado' : 'Applied'}</span>
              {msg.undo ? (
                <button type="button" onClick={() => undoProposal(msgIdx, msg.undo, msg.histId)}
                  title={es ? 'Revertir este cambio' : 'Revert this change'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#2a4a68)', color: 'var(--c-text2,#9fc1dd)', padding: '.3rem .6rem', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: '.72rem', fontFamily: 'inherit' }}>
                  ↩️ {es ? 'Deshacer' : 'Undo'}
                </button>
              ) : null}
            </div>
          ) : state === 'undoing' ? (
            <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>↩️ {es ? 'Deshaciendo…' : 'Undoing…'}</div>
          ) : state === 'undone' ? (
            <div style={{ fontSize: '.74rem', color: '#00c37a', fontWeight: 700 }}>↩️ {es ? 'Cambio deshecho' : 'Change undone'}</div>
          ) : state === 'cancelled' ? (
            <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Cancelado' : 'Cancelled'}</div>
          ) : state === 'error' ? (
            <div style={{ fontSize: '.72rem', color: '#ff8aa3' }}>⚠️ {msg.proposalError || (es ? 'No se pudo aplicar' : 'Could not apply')}</div>
          ) : (
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <button type="button" disabled={state === 'applying'} onClick={() => applyProposal(msgIdx, pr)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'linear-gradient(135deg,#7c5cff,#5b3fd6)', border: 'none', color: '#fff', padding: '.35rem .7rem', borderRadius: 7, cursor: state === 'applying' ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.74rem', opacity: state === 'applying' ? .6 : 1, fontFamily: 'inherit' }}>
                <Check size={13} /> {state === 'applying' ? (es ? 'Aplicando…' : 'Applying…') : (es ? 'Confirmar' : 'Confirm')}
              </button>
              <button type="button" disabled={state === 'applying'} onClick={() => dismissProposal(msgIdx)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#2a4a68)', color: 'var(--c-text2,#9fc1dd)', padding: '.35rem .7rem', borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontSize: '.74rem', fontFamily: 'inherit' }}>
                <X size={13} /> {es ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          )}
        </div>
      );
    });
  };

  // eslint-disable-next-line no-unused-vars
  const _tick = historyTick; // re-read localStorage whenever an action changes
  const renderHistory = () => {
    const items = getAiActions();
    if (!items.length) {
      return (
        <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic', padding: '.5rem' }}>
          {es ? 'Aún no hay acciones. Lo que apliques con el asistente (crear/modificar clientes y pólizas, cambios masivos) aparecerá aquí.' : 'No actions yet. Whatever you apply with the assistant (create/update clients and policies, bulk changes) will show up here.'}
        </div>
      );
    }
    const fmtTime = (at) => { try { return new Date(at).toLocaleString('en-US'); } catch { return ''; } };
    return items.map((e) => {
      const undone = e.status === 'undone';
      const undoing = e.status === 'undoing';
      const entries = Object.entries(e.fields || {});
      const filterEntries = e.filter ? Object.entries(e.filter) : [];
      return (
        <div key={e.id} style={{ marginBottom: '.5rem', border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0d1c2e)', borderRadius: 9, padding: '.55rem .65rem', opacity: undone ? 0.6 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '.5rem' }}>
            <span style={{ fontSize: '.76rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>{titleFor(e.action, e)}</span>
            <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', whiteSpace: 'nowrap' }}>{fmtTime(e.at)}</span>
          </div>
          {e.summary ? <div style={{ fontSize: '.72rem', color: 'var(--c-text2,#9fc1dd)', marginTop: '.2rem', lineHeight: 1.35 }}>{e.summary}</div> : null}
          {filterEntries.length ? (
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: '.2rem' }}>
              {filterEntries.map(([k, v]) => `${fieldLabel(k)}: ${v}`).join(', ')}
            </div>
          ) : null}
          {entries.length ? (
            <div style={{ fontSize: '.7rem', color: 'var(--c-text,#c8dff0)', marginTop: '.2rem' }}>
              {entries.map(([k, v]) => `${fieldLabel(k)}: ${v}`).join(' · ')}
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '.35rem', flexWrap: 'wrap' }}>
            {undone ? (
              <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>↩️ {es ? 'Deshecho' : 'Undone'}</span>
            ) : (
              <span style={{ fontSize: '.68rem', color: '#00c37a', fontWeight: 700 }}>✅ {es ? 'Aplicado' : 'Applied'}</span>
            )}
            {e.undoable && !undone ? (
              <button type="button" disabled={undoing} onClick={() => undoFromHistory(e)}
                title={es ? 'Revertir este cambio' : 'Revert this change'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--c-border,#2a4a68)', color: 'var(--c-text2,#9fc1dd)', padding: '.25rem .55rem', borderRadius: 6, cursor: undoing ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.7rem', fontFamily: 'inherit' }}>
                <RotateCcw size={12} /> {undoing ? (es ? 'Deshaciendo…' : 'Undoing…') : (es ? 'Deshacer' : 'Undo')}
              </button>
            ) : null}
            {e.undoError ? <span style={{ fontSize: '.66rem', color: '#ff8aa3' }}>⚠️ {e.undoError}</span> : null}
          </div>
        </div>
      );
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: embedded ? '100%' : 'min(640px, 70vh)', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '.65rem 1rem', background: 'var(--c-surface,#0d1c2e)', borderBottom: '1px solid var(--c-border,#1a3550)', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88' }} />
        <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{aiName}</span>
        <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)' }}>{es ? '— Siempre disponible' : '— Always available'}</span>
        <button type="button" onClick={() => setShowHistory((v) => !v)}
          title={showHistory ? (es ? 'Volver al chat' : 'Back to chat') : (es ? 'Ver historial de acciones' : 'View action history')}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: showHistory ? 'rgba(0,212,255,.15)' : 'transparent', border: '1px solid var(--c-border,#2a4a68)', color: 'var(--c-text2,#9fc1dd)', padding: '.25rem .55rem', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: '.68rem', fontFamily: 'inherit' }}>
          {showHistory ? <MessageSquare size={13} /> : <History size={13} />}
          {showHistory ? (es ? 'Chat' : 'Chat') : (es ? 'Historial' : 'History')}
        </button>
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '.75rem' }}>
        {showHistory ? renderHistory() : chat.map((msg, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '.5rem' }}>
            <div style={{ maxWidth: '85%', padding: '.55rem .75rem', borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', background: msg.role === 'user' ? 'rgba(0,212,255,.15)' : 'var(--c-surface,#0d1c2e)', border: `1px solid ${msg.role === 'user' ? 'rgba(0,212,255,.25)' : 'var(--c-border,#1a3550)'}`, fontSize: '.8rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
              {msg.text}
            </div>
            {renderProposal(msg, i)}
          </div>
        ))}
        {thinking && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '.5rem' }}>
            <div style={{ padding: '.55rem .75rem', borderRadius: '12px 12px 12px 2px', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.8rem', color: 'var(--c-text3,#45688a)' }}>
              {es ? `${aiName} está escribiendo…` : `${aiName} is typing…`}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '.5rem .75rem', borderTop: '1px solid var(--c-border,#1a3550)', display: 'flex', gap: '.4rem' }}>
        <input className="dkt-inp" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} disabled={thinking}
          placeholder={es ? `Pregunta a ${aiName}...` : `Ask ${aiName}...`} style={{ flex: 1 }} />
        <button type="button" onClick={send} disabled={thinking}
          style={{ background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', padding: '.4rem .8rem', borderRadius: 8, cursor: thinking ? 'wait' : 'pointer', opacity: thinking ? .6 : 1, fontWeight: 700, fontSize: '.78rem', fontFamily: 'inherit' }}>
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
